<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ConversationApi from '../../../api/inbox/conversation';
import ConversationLabelsApi from '../../../api/conversations';
import { kanbanColumns } from './kanban.config';

const route = useRoute();
const router = useRouter();

const cards = ref([]);
const loading = ref(false);
const savingId = ref(null);
const error = ref('');
const draggingId = ref(null);
const activeColumn = ref(null);

const labelFor = column => `kanban_${column}`;
const stageLabels = kanbanColumns.map(column => labelFor(column.id));

const columns = computed(() =>
  kanbanColumns.map(column => ({
    ...column,
    cards: cards.value.filter(card => card.column === column.id),
  }))
);

async function loadConversations() {
  loading.value = true;
  error.value = '';

  try {
    const collected = [];
    const seen = new Set();

    // Conversas abertas de todas as caixas de entrada.
    for (let page = 1; page <= 200; page += 1) {
      const response = await ConversationApi.get({
        inboxId: undefined,
        status: 'open',
        assigneeType: 'all',
        page,
      });

      const data = response.data?.data || response.data;
      const items = data?.payload || [];

      if (!Array.isArray(items) || !items.length) break;

      for (const conversation of items) {
        if (!seen.has(conversation.id)) {
          seen.add(conversation.id);
          collected.push(conversation);
        }
      }

      if (items.length < 25) break;
    }

    // Primeiras 20 conversas resolvidas, ordenadas pela
    // última mudança de status no servidor.
    const response = await ConversationApi.get({
      inboxId: undefined,
      status: 'resolved',
      assigneeType: 'all',
      sortBy: 'status_changed_at_desc',
      page: 1,
    });

    const data = response.data?.data || response.data;
    const resolved = data?.payload || [];

    if (!Array.isArray(resolved)) {
      throw new Error('Resposta inválida da API de conversas resolvidas');
    }

    for (const conversation of resolved.slice(0, 20)) {
      if (!seen.has(conversation.id)) {
        seen.add(conversation.id);
        collected.push(conversation);
      }
    }

    cards.value = collected.map(conversation => {
      const labels = conversation.labels || [];
      const stage = kanbanColumns.find(column =>
        labels.includes(labelFor(column.id))
      );

      return {
        id: conversation.id,
        name: conversation.meta?.sender?.name || `Contato #${conversation.id}`,
        agent: conversation.meta?.assignee?.name || 'Sem responsável',
        detail: `Conversa #${conversation.display_id || conversation.id}`,
        tag: conversation.status || 'open',
        column: stage?.id || 'novo_lead',
        labels: [...labels],
      };
    });
  } catch (e) {
    error.value = 'Não foi possível carregar as conversas.';
    console.error('Kanban:', e);
  } finally {
    loading.value = false;
  }
}

function startDrag(event, card) {
  draggingId.value = card.id;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('text/plain', String(card.id));
}

async function dropCard(event, columnId) {
  event.preventDefault();

  const id = Number(
    event.dataTransfer.getData('text/plain') || draggingId.value
  );
  const card = cards.value.find(item => item.id === id);

  draggingId.value = null;
  activeColumn.value = null;

  if (!card || card.column === columnId || savingId.value) return;

  const previousColumn = card.column;
  savingId.value = id;
  error.value = '';

  try {
    const response = await ConversationLabelsApi.getLabels(id);
    const currentLabels = response.data?.payload || [];

    const updatedLabels = [
      ...currentLabels.filter(label => !stageLabels.includes(label)),
      labelFor(columnId),
    ];

    const result = await ConversationLabelsApi.updateLabels(
      id,
      updatedLabels
    );

    card.labels = result.data?.payload || updatedLabels;
    card.column = columnId;
  } catch (e) {
    card.column = previousColumn;
    error.value = 'Não foi possível salvar a etapa. Verifique as etiquetas do Kanban.';
    console.error('Kanban:', e);
  } finally {
    savingId.value = null;
  }
}

function endDrag() {
  draggingId.value = null;
  activeColumn.value = null;
}

function openConversation(card) {
  if (draggingId.value) return;

  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: card.id,
    },
  });
}

onMounted(loadConversations);
</script>

<template>
  <div class="kanban-page">
    <header class="kanban-header">
      <div>
        <h1>Funil Comercial</h1>
        <p>Painel gerencial das conversas atribuídas aos agentes</p>
      </div>
      <button class="kanban-badge" type="button" @click="loadConversations">
        {{ loading ? 'Carregando...' : 'Atualizar conversas' }}
      </button>
    </header>

    <p v-if="error" role="alert" style="color: #b91c1c; margin-bottom: 12px">
      {{ error }}
    </p>

    <div class="kanban-board">
      <section
        v-for="column in columns"
        :key="column.id"
        class="kanban-column"
        :class="{ 'drag-over': activeColumn === column.id }"
        @dragover.prevent="activeColumn = column.id"
        @dragleave="activeColumn = null"
        @drop="dropCard($event, column.id)"
      >
        <header class="column-header">
          <span>{{ column.title }}</span>
          <span class="column-count">{{ column.cards.length }}</span>
        </header>

        <div class="column-cards">
          <article
            v-for="card in column.cards"
            :key="card.id"
            class="kanban-card"
            :class="{ dragging: draggingId === card.id }"
            :draggable="savingId === null"
            @dragstart="startDrag($event, card)"
            @dragend="endDrag"
            @click="openConversation(card)"
          >
            <div class="card-title">{{ card.name }}</div>
            <div class="card-meta">Agente: {{ card.agent }}</div>
            <div class="card-meta">{{ card.detail }}</div>
            <span class="card-tag">
              {{ savingId === card.id ? 'Salvando...' : card.tag }}
            </span>
          </article>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.kanban-page {
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: 20px;
  background: #f6f7fb;
  color: #1f2937;
}

.kanban-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
}

.kanban-header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
}

.kanban-header p {
  margin-top: 6px;
  color: #6b7280;
  font-size: 14px;
}

.kanban-badge {
  padding: 8px 12px;
  border-radius: 999px;
  background: #eaf4ff;
  color: #0b6fc2;
  font-size: 13px;
}

.kanban-board {
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding-bottom: 14px;
  align-items: stretch;
}

.kanban-column {
  flex: 0 0 260px;
  min-height: 640px;
  padding: 12px;
  background: #eef1f5;
  border: 1px solid #dfe3ea;
  border-radius: 12px;
}

.kanban-column.drag-over {
  outline: 2px solid #1f93ff;
  outline-offset: -2px;
}

.column-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 700;
}

.column-count {
  min-width: 28px;
  padding: 3px 8px;
  text-align: center;
  background: white;
  border: 1px solid #dfe3ea;
  border-radius: 999px;
  font-size: 12px;
}

.column-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 570px;
}

.kanban-card {
  padding: 12px;
  background: white;
  border: 1px solid #dfe3ea;
  border-radius: 10px;
  cursor: grab;
  box-shadow: 0 2px 8px rgba(15,23,42,.05);
}

.kanban-card.dragging {
  opacity: .45;
}

.card-title {
  margin-bottom: 8px;
  font-weight: 700;
}

.card-meta {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.6;
}

.card-tag {
  display: inline-block;
  margin-top: 10px;
  padding: 4px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 11px;
}
</style>
